import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useState, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { getUserFacingApiMessage } from "@/lib/api-client";

import { ScriptReviewAction } from "../ScriptReviewAction";

interface ScriptActionButtonsProps {
  briefUrl: string | null;
  hasUpload: boolean;
  scriptReviewId: string | null;
  preflightUrl: string | null;
  sourceKind?: "script" | "brief";
  onDownloadAsset?: () => Promise<void>;
  className?: string;
  utilityClassName?: string;
  showReview?: boolean;
  reviewAriaLabel?: string;
}

function IconAction({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

const ICON_LINK_CLASS =
  "inline-flex size-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40";

/** Icon-only secondary actions for one script; the tooltip carries the label. */
export function ScriptActionButtons({
  briefUrl,
  hasUpload,
  scriptReviewId,
  preflightUrl,
  sourceKind = "script",
  onDownloadAsset,
  className,
  utilityClassName,
  showReview = true,
  reviewAriaLabel,
}: ScriptActionButtonsProps) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const downloadLabel =
    sourceKind === "brief"
      ? t("marketplace.orders.list.downloadBrief", "Download brief")
      : t("marketplace.orders.list.downloadScript", "Download script");
  const hasReview = showReview && Boolean(scriptReviewId || preflightUrl);

  const handleDownload = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!onDownloadAsset || downloading) return;
    setDownloading(true);
    try {
      await onDownloadAsset();
    } catch (error) {
      toast.error(getUserFacingApiMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  const stop = (event: MouseEvent) => event.stopPropagation();

  return (
    <span
      className={cn("inline-flex items-center justify-end gap-0.5", className)}
    >
      {!hasReview ? (
        <span
          className={cn("inline-flex items-center gap-0.5", utilityClassName)}
        >
          {hasUpload && onDownloadAsset ? (
            <IconAction label={downloadLabel}>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={downloadLabel}
                aria-busy={downloading || undefined}
                disabled={downloading}
                className="text-muted-foreground hover:text-foreground"
                onClick={(event) => void handleDownload(event)}
              >
                <Icon
                  name={downloading ? "loader" : "download"}
                  size="sm"
                  color="inherit"
                  className={downloading ? "animate-spin" : undefined}
                />
              </Button>
            </IconAction>
          ) : null}
          {briefUrl ? (
            <IconAction label={t("marketplace.orders.list.openBriefLink")}>
              <a
                href={briefUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={t("marketplace.orders.list.openBriefLink")}
                onClick={stop}
                className={ICON_LINK_CLASS}
              >
                <Icon name="external-link" size="sm" color="inherit" />
              </a>
            </IconAction>
          ) : null}
        </span>
      ) : null}
      {hasReview ? (
        <ScriptReviewAction
          scriptReviewId={scriptReviewId}
          preflightUrl={preflightUrl}
          label={t("marketplace.orders.list.review", "Review")}
          ariaLabel={
            reviewAriaLabel ??
            t(
              "marketplace.orders.list.reviewVideoAriaGeneric",
              "Review video (opens in a new tab)",
            )
          }
          variant="default"
          size="sm"
          compact
        />
      ) : null}
    </span>
  );
}
